import React, { useEffect, useState } from "react";
import {
  Drawer,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
  Typography,
  Box,
  IconButton,
  Avatar,
  Badge,
} from "@mui/material";
import { useLocation, useNavigate } from "react-router-dom";
import HomeIcon from "@mui/icons-material/Home";
import SettingsIcon from "@mui/icons-material/Settings";
import ExitToAppIcon from "@mui/icons-material/ExitToApp";
import LoginIcon from "@mui/icons-material/Login";
import EventIcon from "@mui/icons-material/Event";
import PeopleAltIcon from "@mui/icons-material/PeopleAlt";
import VideogameAssetIcon from "@mui/icons-material/VideogameAsset";
import AppRegistrationIcon from "@mui/icons-material/AppRegistration";
import FestivalIcon from "@mui/icons-material/Festival";
import GroupsIcon from "@mui/icons-material/Groups";
import HelpCenterIcon from "@mui/icons-material/HelpCenter";
import ArrowBackIosIcon from "@mui/icons-material/ArrowBackIos";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import PollIcon from "@mui/icons-material/Poll";
import FastfoodIcon from "@mui/icons-material/Fastfood";
import WorkspacePremiumIcon from "@mui/icons-material/WorkspacePremium";
import ChatIcon from "@mui/icons-material/Chat";
import EngineeringIcon from "@mui/icons-material/Engineering";
import MapIcon from "@mui/icons-material/Map";
import PersonSearchIcon from '@mui/icons-material/PersonSearch';
import { useAuth } from "../../context/auth.context.js";
import logo from "../../assets/logo512.png";
import { useWebSocket } from "../../context/websocket.context.js";

const drawerWidthOpen = 240;
const drawerWidthClosed = 60;

const Navigation = () => {
  const [open, setOpen] = useState(true);
  const navigate = useNavigate();
  const auth = useAuth();
  const location = useLocation();
  const [unreadChat, setUnreadChat] = useState(false);
  const [lastMessage, SetLastMessage] = useState();
  const websocket = useWebSocket();

  useEffect(() => {
    if (!websocket || !websocket.message) {
      return;
    }
    if (JSON.stringify(websocket.message) === JSON.stringify(lastMessage)) {
      if (websocket.chatRead) {
        setUnreadChat(false);
      }
      return;
    }
    if (websocket.message.Type === "ChatMessage") {
      setUnreadChat(true);
      SetLastMessage(websocket.message);
    }
  }, [websocket]);

  const handleNavigationClick = (target) => {
    navigate(target);
  };

  const handleNavigationWithStateClick = (target, state) => {
    navigate(target, state);
  };

  const toggleDrawer = () => {
    setOpen(!open);
  };

  return (
    <Drawer
      variant="permanent"
      sx={{
        width: open ? drawerWidthOpen : drawerWidthClosed,
        flexShrink: 0,
        "& .MuiDrawer-paper": {
          width: open ? drawerWidthOpen : drawerWidthClosed,
          boxSizing: "border-box",
          transition: "width 0.3s",
          overflow: "hidden",
          overflowY: "auto",
        },
      }}
    >
      <List
        style={{
          display: "flex",
          flexDirection: "column",
          height: "100vh",
          padding: 0,
        }}
      >
        <img width="100%" height="auto" src={logo} alt="logo" loading="lazy" />

        {!open && (
          <ListItem>
            <Box sx={{
              display: "flex",
              justifyContent: open ? "space-between" : "center",
              alignItems: "center",
              width: "100%"
            }}>
              <IconButton fontSize="small" onClick={toggleDrawer}>
                <ArrowForwardIosIcon />
              </IconButton>
            </Box>
          </ListItem>
        )}

        {open && (
          <ListItem onClick={() => console.log("Auth", auth.user)}>
            <Box sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center"
            }}>
              <IconButton fontSize="small" onClick={toggleDrawer}>
                <ArrowBackIosIcon />
              </IconButton>
              <Typography variant="h4" color="primary">
                LANigator
              </Typography>
            </Box>
          </ListItem>
        )}

        <Divider />

        {auth.user && (
          <ListItem
            onClick={() => handleNavigationClick("/")}
            sx={(theme) => ({
              cursor: "pointer",
              borderLeft:
                location.pathname === "/" || location.pathname === "/tournament"
                  ? `2px solid ${theme.palette.primary.main}`
                  : "none",
              "&:hover": {
                backgroundColor: theme.palette.primary.light,
              },
            })}
          >
            <ListItemIcon>
              <HomeIcon />
            </ListItemIcon>
            {open && <ListItemText primary="Dashboard" />}
          </ListItem>
        )}

        {auth.user && (
          <ListItem
            onClick={() => handleNavigationClick("/gather")}
            sx={(theme) => ({
              cursor: "pointer",
              borderLeft:
                location.pathname === "/gather"
                  ? `2px solid ${theme.palette.primary.main}`
                  : "none",
              "&:hover": {
                backgroundColor: theme.palette.primary.light,
              },
            })}
          >
            <ListItemIcon>
              <PersonSearchIcon />
            </ListItemIcon>
            {open && <ListItemText primary="Gather" />}
          </ListItem>
        )}

        {auth.user && (
          <ListItem
            onClick={() => handleNavigationClick("/order")}
            sx={(theme) => ({
              cursor: "pointer",
              borderLeft:
                location.pathname === "/order"
                  ? `2px solid ${theme.palette.primary.main}`
                  : "none",
              "&:hover": {
                backgroundColor: theme.palette.primary.light,
              },
            })}
          >
            <ListItemIcon>
              <FastfoodIcon />
            </ListItemIcon>
            {open && <ListItemText primary="Order" />}
          </ListItem>
        )}

        {auth.user && (
          <ListItem
            onClick={() => handleNavigationClick("/chat")}
            sx={(theme) => ({
              cursor: "pointer",
              borderLeft:
                location.pathname === "/chat"
                  ? `2px solid ${theme.palette.primary.main}`
                  : "none",
              "&:hover": {
                backgroundColor: theme.palette.primary.light,
              },
            })}
          >
            <ListItemIcon>
              <Badge
                color="secondary"
                invisible={!unreadChat}
                badgeContent=""
                anchorOrigin={{
                  vertical: "top",
                  horizontal: "left",
                }}
              >
                <ChatIcon />
              </Badge>
            </ListItemIcon>
            {open && <ListItemText primary="Chat" />}
          </ListItem>
        )}

        {auth.user && (
          <ListItem
            onClick={() => handleNavigationClick("/location")}
            sx={(theme) => ({
              cursor: "pointer",
              borderLeft:
                location.pathname === "/location"
                  ? `2px solid ${theme.palette.primary.main}`
                  : "none",
              "&:hover": {
                backgroundColor: theme.palette.primary.light,
              },
            })}
          >
            <ListItemIcon>
              <MapIcon />
            </ListItemIcon>
            {open && <ListItemText primary="Location" />}
          </ListItem>
        )}

        {auth.user && (
          <ListItem
            onClick={() => handleNavigationClick("/faq")}
            sx={(theme) => ({
              cursor: "pointer",
              borderLeft:
                location.pathname === "/faq"
                  ? `2px solid ${theme.palette.primary.main}`
                  : "none",
              "&:hover": {
                backgroundColor: theme.palette.primary.light,
              },
            })}
          >
            <ListItemIcon>
              <HelpCenterIcon />
            </ListItemIcon>
            {open && <ListItemText primary="FAQ" />}
          </ListItem>
        )}

        {!auth.user && (
          <ListItem
            onClick={() => handleNavigationClick("/login")}
            sx={(theme) => ({
              cursor: "pointer",
              borderLeft:
                location.pathname === "/login"
                  ? `2px solid ${theme.palette.primary.main}`
                  : "none",
              "&:hover": {
                backgroundColor: theme.palette.primary.light,
              },
            })}
          >
            <ListItemIcon>
              <LoginIcon />
            </ListItemIcon>
            {open && <ListItemText primary="Login" />}
          </ListItem>
        )}
        {!auth.user && (
          <ListItem
            onClick={() => handleNavigationClick("/register")}
            sx={(theme) => ({
              cursor: "pointer",
              borderLeft:
                location.pathname === "/register"
                  ? `2px solid ${theme.palette.primary.main}`
                  : "none",
              "&:hover": {
                backgroundColor: theme.palette.primary.light,
              },
            })}
          >
            <ListItemIcon>
              <AppRegistrationIcon />
            </ListItemIcon>
            {open && <ListItemText primary="Registration" />}
          </ListItem>
        )}

        <Divider />

        {auth.user && auth.user.role === "Admin" && (
          <ListItem>
            <ListItemIcon>
              <EngineeringIcon color="primary" />
            </ListItemIcon>
            {open && (
              <Typography variant="h6" color="primary">
                Administration
              </Typography>
            )}
          </ListItem>
        )}
        {auth.user && auth.user.role === "Admin" && (
          <ListItem
            onClick={() => handleNavigationClick("/admin/events")}
            sx={(theme) => ({
              cursor: "pointer",
              borderLeft:
                location.pathname === "/admin/events"
                  ? `2px solid ${theme.palette.primary.main}`
                  : "none",
              "&:hover": {
                backgroundColor: theme.palette.primary.light,
              },
            })}
          >
            <ListItemIcon>
              <EventIcon />
            </ListItemIcon>
            {open && <ListItemText primary="Events" />}
          </ListItem>
        )}
        {auth.user && auth.user.role === "Admin" && (
          <ListItem
            onClick={() => handleNavigationClick("/admin/tournaments")}
            sx={(theme) => ({
              cursor: "pointer",
              borderLeft:
                location.pathname === "/admin/tournaments"
                  ? `2px solid ${theme.palette.primary.main}`
                  : "none",
              "&:hover": {
                backgroundColor: theme.palette.primary.light,
              },
            })}
          >
            <ListItemIcon>
              <FestivalIcon />
            </ListItemIcon>
            {open && <ListItemText primary="Tournaments" />}
          </ListItem>
        )}
        {auth.user && auth.user.role === "Admin" && (
          <ListItem
            onClick={() => handleNavigationClick("/admin/games")}
            sx={(theme) => ({
              cursor: "pointer",
              borderLeft:
                location.pathname === "/admin/games"
                  ? `2px solid ${theme.palette.primary.main}`
                  : "none",
              "&:hover": {
                backgroundColor: theme.palette.primary.light,
              },
            })}
          >
            <ListItemIcon>
              <VideogameAssetIcon />
            </ListItemIcon>
            {open && <ListItemText primary="Games" />}
          </ListItem>
        )}
        {auth.user && auth.user.role === "Admin" && (
          <ListItem
            onClick={() => handleNavigationClick("/admin/users")}
            sx={(theme) => ({
              cursor: "pointer",
              borderLeft:
                location.pathname === "/admin/users"
                  ? `2px solid ${theme.palette.primary.main}`
                  : "none",
              "&:hover": {
                backgroundColor: theme.palette.primary.light,
              },
            })}
          >
            <ListItemIcon>
              <PeopleAltIcon />
            </ListItemIcon>
            {open && <ListItemText primary="Users" />}
          </ListItem>
        )}

        {auth.user && auth.user.role === "Admin" && (
          <ListItem
            onClick={() => handleNavigationClick("/admin/teams")}
            sx={(theme) => ({
              cursor: "pointer",
              borderLeft:
                location.pathname === "/admin/teams"
                  ? `2px solid ${theme.palette.primary.main}`
                  : "none",
              "&:hover": {
                backgroundColor: theme.palette.primary.light,
              },
            })}
          >
            <ListItemIcon>
              <GroupsIcon />
            </ListItemIcon>
            {open && <ListItemText primary="Teams" />}
          </ListItem>
        )}

        {auth.user && auth.user.role === "Admin" && (
          <ListItem
            onClick={() => handleNavigationClick("/admin/awards")}
            sx={(theme) => ({
              cursor: "pointer",
              borderLeft:
                location.pathname === "/admin/awards"
                  ? `2px solid ${theme.palette.primary.main}`
                  : "none",
              "&:hover": {
                backgroundColor: theme.palette.primary.light,
              },
            })}
          >
            <ListItemIcon>
              <WorkspacePremiumIcon />
            </ListItemIcon>
            {open && <ListItemText primary="Awards" />}
          </ListItem>
        )}

        {auth.user && auth.user.role === "Admin" && (
          <ListItem
            onClick={() => handleNavigationClick("/admin/polls")}
            sx={(theme) => ({
              cursor: "pointer",
              borderLeft:
                location.pathname === "/admin/polls"
                  ? `2px solid ${theme.palette.primary.main}`
                  : "none",
              "&:hover": {
                backgroundColor: theme.palette.primary.light,
              },
            })}
          >
            <ListItemIcon>
              <PollIcon />
            </ListItemIcon>
            {open && <ListItemText primary="Polls" />}
          </ListItem>
        )}

        <Divider />

        {auth.user && (
          <ListItem
            style={{ marginTop: "auto" }}
            onClick={() =>
              handleNavigationWithStateClick("/profile", {
                state: { userId: auth.user.id },
              })
            }
            sx={(theme) => ({
              cursor: "pointer",
              borderLeft:
                location.pathname === "/profile"
                  ? `2px solid ${theme.palette.primary.main}`
                  : "none",
              "&:hover": {
                backgroundColor: theme.palette.primary.light,
              },
            })}
          >
            <Avatar
              sx={{
                width: 28,
                height: 28,
                border: 1,
                borderColor: "primary.main",
              }}
              alt={auth.user.nickname}
              src={`/static/images/${
                auth.user.avatarFileName ?? "default.jpg"
              }`}
            />
            {open && (
              <ListItemText
                sx={{ marginLeft: 1 }}
                primary={auth.user.nickname}
              />
            )}
          </ListItem>
        )}
        {auth.user && (
          <ListItem
            style={{ marginTop: "" }}
            onClick={() => handleNavigationClick("/profile/settings")}
            sx={(theme) => ({
              cursor: "pointer",
              borderLeft:
                location.pathname === "/profile/settings"
                  ? `2px solid ${theme.palette.primary.main}`
                  : "none",
              "&:hover": {
                backgroundColor: theme.palette.primary.light,
              },
            })}
          >
            <ListItemIcon>
              <SettingsIcon />
            </ListItemIcon>
            {open && <ListItemText primary="Settings" />}
          </ListItem>
        )}
        {auth.user && (
          <ListItem
            onClick={() => auth.logout()}
            sx={(theme) => ({
              cursor: "pointer",
              "&:hover": {
                backgroundColor: theme.palette.primary.light,
              },
            })}
          >
            <ListItemIcon>
              <ExitToAppIcon />
            </ListItemIcon>
            {open && <ListItemText primary="Logout" />}
          </ListItem>
        )}
      </List>
    </Drawer>
  );
};

export default Navigation;
